import { Directive, AfterViewChecked, inject, Input } from '@angular/core';

import { Panel } from 'primeng/panel';
import { HisUserProfileService } from '../service/backend/his-userprofile.service';
import { HisAuthService } from '../../auth/service/his-auth.service';
import { OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-panel',
    standalone: false
})
export class HisPanelDirective implements AfterViewChecked, OnDestroy {
    private userProfileService = inject(HisUserProfileService);
    private cdRef = inject(ChangeDetectorRef);

    private panelComponent = inject(Panel, { host: true, optional: true, self: true });
    private panelStateInitialized = false;
    private panelStateSubscription: Subscription;
    private hisAuthService = inject(HisAuthService);
    
    @Input() skipSavePanelState: boolean = false;

    ngAfterViewChecked() {
        if (this.panelComponent && (this.panelComponent as any).toggleable && !this.panelStateInitialized) {
            this.initializePanelState();
            if (!this.panelStateSubscription && !this.hisAuthService.isAnonymous) {
                this.panelStateSubscription = (this.panelComponent as any).collapsedChange.subscribe((collapsed: boolean) => {
                    this.savePanelState(collapsed);
                });
            }
        } else {
            this.managePanelVisibility();
        }
    }

    private initializePanelState(): void {
        if (this.panelComponent && (this.panelComponent as any).toggleable) {
            // Das Identifizieren der Panels anhand ihrer ID funktioniert leider nicht, da sich die IDs bei jedem Seitenaufruf ändern
            // Also ziehen wir den Header heran 
            const sanitizedHeader = this.sanitizeKey((this.panelComponent as any).header);
            let sanitizedPanelText;
            if (!sanitizedHeader) {
                sanitizedPanelText = this.sanitizeKey((this.panelComponent as any).collapseText);
            }
            const key = sanitizedHeader? `panel_${sanitizedHeader}_state` : `panel_${sanitizedPanelText}_state`;

            this.userProfileService.getValueforKey(key).subscribe((savedState: string) => {
                if (savedState !== null && savedState !== undefined) {
                    const isCollapsed = savedState === 'collapsed';
                    if (isCollapsed) {
                        (this.panelComponent as any).collapsed = isCollapsed;
                        // Manuell die Änderungserkennung auslösen, da Panel sonst initial nicht im korrekten Zustand dargestellt wird
                        this.cdRef.detectChanges();
                    }
                }
                this.panelStateInitialized = true;
            });
        }
    }

    private savePanelState(collapsed: boolean): void {
        if (this.panelComponent && (this.panelComponent as any).toggleable && !this.skipSavePanelState) {
            const sanitizedHeader = this.sanitizeKey((this.panelComponent as any).header);
            let sanitizedPanelText;
            if (!sanitizedHeader) {
                sanitizedPanelText = this.sanitizeKey((this.panelComponent as any).collapseText);
            }
            const key = sanitizedHeader? `panel_${sanitizedHeader}_state` : `panel_${sanitizedPanelText}_state`;
            const state = collapsed ? 'collapsed' : 'expanded';
            this.userProfileService.setNewValue(key, state, 'expanded').subscribe(() => { });
        }
    }

    private sanitizeKey(input: string): string {
        // Ersetze alle nicht alphanumerischen Zeichen durch einen Unterstrich
        return input?.replace(/[^a-zA-Z0-9 ]/g, '_')?.replace(/\s+/g, '_');
    }

    private managePanelVisibility(): void {
        // console.log('#PanelDirective.ngAfterViewChecked() - panelComponent: ', ( this.panelComponent as any ) );
        if ( this.panelComponent ) {
            if (  this.isPanelToggleableAndNotCollapsed() && this.isPanelHiddenOrEmpty() ) {
                // console.log('#PanelDirective has not content except header ', ( this.panelComponent as a<ny ).el.nativeElement.innerText );
                ( this.panelComponent as any ).el.nativeElement.hidden = true;
            } else {
                ( this.panelComponent as any ).el.nativeElement.hidden = false;
            }
        }
    }

    private isPanelHiddenOrEmpty(): boolean {
        return ( ( this.panelComponent as any ).ddHidden || ( this.panelComponent as any ).el.nativeElement.innerText === ( this.panelComponent as any ).header );
    }

    private isPanelToggleableAndNotCollapsed(): boolean {
        return ( this.panelComponent as any ).toggleable === false || ( this.panelComponent as any ).toggleable === true && !( this.panelComponent as any ).collapsed;
    }

    ngOnDestroy() {
        if (this.panelStateSubscription) {
            this.panelStateSubscription.unsubscribe();
        }
    }
}
